import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { NodeCodeEditor } from "../../../NodeUI";
import useCanvasStore from "../useCanvasStore";
import { getComponentLineMap } from "../../../../utils/workflowDefinitionYaml";
import useWorkflowYamlSnapshot from "../../../../hooks/useWorkflowYamlSnapshot";
import "./WorkflowCodeView.css";

export default function WorkflowCodePane({
  projectId,
  workflow,
  components,
  production,
  saving = false,
  savedComponent,
  lastModified,
  onClose,
}) {
  const [editor, setEditor] = useState(null);
  const [showMetadata, setShowMetadata] = useState(false);
  const scrollAnimationRef = useRef(null);
  const codePaneNavigation = useCanvasStore(
    (state) => state.codePaneNavigation
  );
  const { snapshot, actions } = useWorkflowYamlSnapshot({
    projectId,
    workflow,
    components,
    production,
    saving,
    viewMode: showMetadata ? "persisted" : "live",
    persistedRefreshKey: `${savedComponent}:${lastModified}`,
  });
  const code = snapshot.yaml;
  const componentLines = useMemo(
    () => getComponentLineMap(code, components),
    [code, components]
  );

  useEffect(() => {
    const row = componentLines[codePaneNavigation.componentId];
    if (!editor || row === undefined) return;

    const canAnimateScroll =
      typeof requestAnimationFrame === "function" &&
      editor.session?.documentToScreenRow &&
      editor.session?.getScrollTop &&
      editor.session?.setScrollTop;

    if (!canAnimateScroll) {
      editor.scrollToLine?.(row, false, true);
    } else {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
      const lineHeight =
        editor.renderer?.lineHeight ??
        editor.renderer?.layerConfig?.lineHeight ??
        16;
      const targetScrollTop =
        editor.session.documentToScreenRow(row, 0) * lineHeight;
      const initialScrollTop = editor.session.getScrollTop();
      const startedAt = performance.now();
      const durationMs = 240;

      const animateScroll = (now) => {
        const progress = Math.min(1, (now - startedAt) / durationMs);
        const eased = 1 - Math.pow(1 - progress, 3);
        editor.session.setScrollTop(
          initialScrollTop + (targetScrollTop - initialScrollTop) * eased
        );
        if (progress < 1) {
          scrollAnimationRef.current = requestAnimationFrame(animateScroll);
        } else {
          scrollAnimationRef.current = null;
        }
      };
      scrollAnimationRef.current = requestAnimationFrame(animateScroll);
    }

    let markerId = null;
    let markerTimeout = null;
    try {
      const Range = editor.selection?.getRange()?.constructor;
      if (Range && editor.session?.addMarker) {
        markerId = editor.session.addMarker(
          new Range(row, 0, row, 1),
          "workflow-code-selection-flash",
          "fullLine",
          false
        );
        markerTimeout = window.setTimeout(() => {
          if (markerId !== null) {
            editor.session.removeMarker?.(markerId);
            markerId = null;
          }
        }, 900);
      }
    } catch (_error) {
      // Selection scrolling should still work if Ace marker APIs are unavailable.
    }

    return () => {
      if (markerTimeout !== null) window.clearTimeout(markerTimeout);
      if (markerId !== null) editor.session?.removeMarker?.(markerId);
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
    };
  }, [
    codePaneNavigation.componentId,
    codePaneNavigation.sequence,
    componentLines,
    editor,
  ]);

  return (
    <section
      className="workflow-code-pane"
      aria-label="Workflow code"
      data-testid="workflow-code-pane"
    >
      <header className="workflow-code-pane__header">
        <Typography component="h2" className="workflow-code-pane__title">
          YAML
        </Typography>
        <div className="workflow-code-pane__actions">
          <FormControlLabel
            className="workflow-code-pane__metadata-toggle"
            label="Show metadata"
            control={
              <Switch
                size="small"
                checked={showMetadata}
                onChange={(event) => setShowMetadata(event.target.checked)}
                inputProps={{ "aria-label": "Show workflow metadata" }}
              />
            }
          />
          <Tooltip title={actions.copyStatus || "Copy saved workflow YAML"}>
            <span>
              <IconButton
                size="small"
                onClick={actions.copyPersistedYaml}
                aria-label="Copy saved workflow YAML"
                className="workflow-code-pane__action"
                disabled={!projectId || saving || actions.copying}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download saved workflow YAML">
            <span>
              <IconButton
                size="small"
                onClick={actions.downloadPersistedYaml}
                aria-label="Download saved workflow YAML"
                className="workflow-code-pane__action"
                disabled={!projectId || saving || actions.exporting}
              >
                {actions.exporting ? (
                  <CircularProgress size={18} />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close code view">
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close code view"
              className="workflow-code-pane__close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </header>
      {snapshot.error && (
        <Alert severity="error" className="workflow-code-pane__error">
          {snapshot.error}
        </Alert>
      )}
      <div className="workflow-code-pane__editor">
        {snapshot.source === "persisted" &&
        snapshot.loading &&
        !snapshot.yaml ? (
          <div className="workflow-code-pane__loading">
            <CircularProgress size={20} />
          </div>
        ) : (
          <NodeCodeEditor
            value={code}
            language="yaml"
            readOnly
            disableExpand
            fillContainer
            showGutter
            showPrintMargin={false}
            wrapEnabled
            onLoad={setEditor}
          />
        )}
      </div>
    </section>
  );
}
